import React, { useState, useEffect, useCallback } from 'react';
import { Button, Spinner, Form, Row, Col, Table } from 'react-bootstrap';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import adminApi, { getAdminLookups } from '../../services/adminApi'; // Assuming this path is correct
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import './AdminCostPage.css'; // Assuming you have this CSS file

// Helper to parse JSON safely - used for displaying data in the table
const parseDetails = (details) => {
  try {
    // If details is already an object/array, return it. Otherwise, parse.
    if (typeof details === 'object' && details !== null) {
      return details;
    }
    // If it's a string, try to parse it. Handle empty strings or invalid JSON.
    if (typeof details === 'string') {
      return JSON.parse(details || '[]');
    }
    return []; // Return empty array for null, undefined, or other types
  } catch (e) {
    console.error("Frontend: Error parsing cost details JSON:", e);
    return []; // Return empty array on error
  }
};

// Helper to format type names (e.g., 'auditionType' -> 'Audition Type')
const formatTypeName = (name) => {
  if (!name) return '';
  return name
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/_/g, ' ')      // Replace underscores with spaces
    .replace(/^./, str => str.toUpperCase()); // Capitalize first letter
};

const AdminCostPage = () => {
  const [costs, setCosts] = useState([]);
  const [lookupGroups, setLookupGroups] = useState([]);
  const [view, setView] = useState('list'); // 'list' or 'form'
  const [currentCost, setCurrentCost] = useState(null); // Holds the item being edited
  const [loading, setLoading] = useState(false);
  const [originalDetails, setOriginalDetails] = useState([]); // To track original details when editing
  const [typeChangedDuringEdit, setTypeChangedDuringEdit] = useState(false); // Track if type changed while editing

  // State for modal type ('new' or 'edit')
  const [modalType, setModalType] = useState('new');

  // --- React Hook Form Setup ---
  const {
    control, // For Controller components
    handleSubmit, // Function to call on form submit
    reset, // Function to reset form fields
    setValue, // Function to set form field values programmatically
    watch, // Function to watch field values
    formState: { errors } // Access form validation errors
  } = useForm({
    // Ensure unregister is false so values persist when switching views
    shouldUnregister: false,
    defaultValues: {
      title: '',      // <-- Matches Controller name="title"
      type: '',       // <-- Matches Controller name="type"
      valueType: '',
      details: [] // Initialize details as an empty array
    }
  });

  // useFieldArray manages the 'details' array
  const { fields, append, remove, swap } = useFieldArray({
    control, // <-- Provided by useForm
    name: "details" // Matches the name in defaultValues
  });

  // Watch 'type' and 'valueType' to trigger side effects
  const watchedType = watch("type");
  const watchedValueType = watch("valueType");

  // --- Data Fetching ---
  const fetchCosts = useCallback(async () => {
    console.log("Frontend: Fetching costs...");
    try {
      const res = await adminApi.get('/cost-list');
      setCosts(res.data);
      console.log("Frontend: Costs fetched successfully.", res.data.length);
    } catch (e) {
      console.error("Frontend: Error fetching costs:", e);
      // Consider showing an error message to the user
    }
  }, []);

  const fetchLookups = useCallback(async () => {
    console.log("Frontend: Fetching lookups...");
    try {
      const res = await getAdminLookups();
      // Filter lookups relevant for cost types
      const filtered = res.data.filter(lk =>
        ['auditionType', 'jobRoleCategory', 'projectType'].includes(lk.lookupGroup)
      );
      setLookupGroups(filtered);
      console.log("Frontend: Lookups fetched and filtered.", filtered.length);
    } catch (e) {
      console.error("Frontend: Error fetching lookups:", e);
      // Consider showing an error message to the user
    }
  }, []);

  // Fetch initial data when the component mounts
  useEffect(() => {
    fetchCosts();
    fetchLookups();
  }, [fetchCosts, fetchLookups]);

  // --- Form Logic ---

  // Effect to populate 'details' when 'type' changes
  useEffect(() => {
    if (!watchedType) return; // Do nothing if type is not selected

    console.log(`Frontend: Type changed to "${watchedType}".`);
    const selectedGroup = lookupGroups.find(g => g.lookupGroup === watchedType);

    if (selectedGroup && selectedGroup.lookupValue && selectedGroup.lookupValue.data) {
      // Map lookup values to the expected { value: '', label: d.lookupLabel } format
      const newDetails = selectedGroup.lookupValue.data.map(d => ({
        value: '', // Initialize value as empty
        label: d.lookupLabel
      }));

      setValue('details', newDetails); // Update form state
      console.log("Frontend: Details populated based on new type.");

      // If editing and the type has changed, mark it
      if (view === 'form' && currentCost && modalType === 'edit') {
        setTypeChangedDuringEdit(true);
        console.log("Frontend: Type changed during edit, flagged.");
      }
    }
  }, [watchedType, lookupGroups, setValue, view, currentCost, modalType]);

  // Effect to handle 'valueType' changes, especially during edit
  useEffect(() => {
    // Only apply logic if we are in edit mode and the type hasn't changed
    if (view === 'form' && modalType === 'edit' && !typeChangedDuringEdit) {
      console.log(`Frontend: ValueType changed to "${watchedValueType}". Type has not changed.`);
      if (watchedValueType === currentCost?.valueType) {
        // If valueType matches the original, restore original details
        console.log("Frontend: ValueType matches original, restoring original details.");
        setValue('details', originalDetails);
      } else {
        // If valueType is different, clear all values in details
        console.log("Frontend: ValueType differs, clearing values in details.");
        const blankDetails = originalDetails.map(d => ({
          label: d.label,
          value: '' // Clear the value
        }));
        setValue('details', blankDetails);
      }
    } else if (view === 'form' && modalType === 'edit' && typeChangedDuringEdit) {
        console.log("Frontend: ValueType changed, but type was also changed during edit, skipping valueType logic.");
    }
  }, [watchedValueType, setValue, originalDetails, currentCost, modalType, typeChangedDuringEdit, view]);

  


  // --- Handlers ---

  // Handle form submission (Save or Update)
  const onSubmit = async (data) => {
    console.log("Frontend: Form submitted. Data:", data); // Log to verify data before sending
    setLoading(true);

    // Ensure details is always an array before sending
    const payload = {
      ...data,
      details: Array.isArray(data.details) ? data.details : []
    };

    try {
      if (modalType === 'edit') {
        console.log(`Frontend: Updating cost with ID: ${currentCost.id}`);
        await adminApi.put(`/cost/${currentCost.id}`, payload);
        console.log("Frontend: Update successful.");
      } else {
        console.log("Frontend: Saving new cost.");
        await adminApi.post('/cost-save', payload);
        console.log("Frontend: Save successful.");
      }

      // Refresh the list and switch back to list view
      fetchCosts();
      setView('list');
      resetForm(); // Reset form state
    } catch (e) {
      console.error("Frontend: Error saving/updating cost:", e);
      // Display error message to user
      alert(`Failed to save cost: ${e.response?.data?.message || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle clicking the "Add" button
  const handleAdd = () => {
    setModalType('new'); // Set modalType to 'new'
    setCurrentCost(null);
    setTypeChangedDuringEdit(false); // Reset flag
    setOriginalDetails([]); // Clear original details
    resetForm(); // Reset form fields
    setView('form'); // Switch to form view
    console.log("Frontend: Switching to 'Add Cost' form.");
  };

  // Handle clicking the "Edit" button
  const handleEdit = (item) => {
    setModalType('edit'); // Set modalType to 'edit'
    setCurrentCost(item);
    setTypeChangedDuringEdit(false); // Reset flag

    const parsedDetails = parseDetails(item.details);
    setOriginalDetails(parsedDetails); // Store original details for comparison

    // Populate form with item's data
    reset({
      title: item.title,
      type: item.type,
      valueType: item.valueType || '',
      details: parsedDetails
    });

    setView('form'); // Switch to form view
    console.log("Frontend: Switching to 'Edit Cost' form for ID:", item.id);
  };

  // Handle clicking the "Delete" button
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this cost item?")) return;

    console.log(`Frontend: Deleting cost with ID: ${id}`);
    try {
      await adminApi.delete(`/cost/${id}`);
      console.log(`Frontend: Successfully deleted cost ID: ${id}`);
      fetchCosts(); // Refresh the list
    } catch (e) {
      console.error(`Frontend: Error deleting cost ID ${id}:`, e);
      alert(`Failed to delete cost: ${e.response?.data?.message || e.message}`);
    }
  };

  // Reset form and clear states
  const resetForm = () => {
    reset({
      title: '',
      type: '',
      valueType: '',
      details: []
    });
    setCurrentCost(null);
    setOriginalDetails([]);
    setTypeChangedDuringEdit(false);
    console.log("Frontend: Form reset.");
  };

  // Handle clicking the "Cancel" button
  const handleCancel = () => {
    if (!window.confirm("Discard unsaved changes?")) return;
    resetForm();
    setView('list'); // Switch back to list view
    console.log("Frontend: Cancelled form. Switching back to list view.");
  };

  // --- Render Logic ---
  return (
    <div className="p-4 bg-dark text-white">

      {/* LIST VIEW */}
      {view === 'list' ? (
        <>
          <div className="d-flex justify-content-between mb-3">
            <h3>Cost Management</h3>
            <Button className="btn-add-cost" onClick={handleAdd}>
              <FaPlus /> Add New Cost
            </Button>
          </div>

          <Table bordered variant="dark" responsive="sm">
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Value Type</th>
                <th>Items</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {costs.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center">No cost items found.</td>
                </tr>
              ) : (
                costs.map(c => (
                  <tr key={c.id}>
                    <td>{c.title}</td>
                    <td>{formatTypeName(c.type)}</td>
                    <td>
                      {c.valueType === 'percentage' ? 'Percentage (%)'
                      : c.valueType === 'fix' ? 'Fixed'
                      : '-'}
                    </td>
                    <td>
                      {parseDetails(c.details)
                        .filter(d => d.value !== undefined && d.value !== '') // Only show items with a value
                        .map((d, i) => (
                          <div key={i}>
                            <strong>{d.label}</strong>: {d.value}
                          </div>
                        ))}
                    </td>
                    <td className="actions-cell">
                      <div className="actions-wrapper">
                        <Button size="sm" className="btn-edit me-2" onClick={() => handleEdit(c)}>
                          <FaEdit /> Edit
                        </Button>
                        <Button size="sm" variant="danger" className="btn-delete" onClick={() => handleDelete(c.id)}>
                          <FaTrash /> Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </>
      ) : (
        /* FULL PAGE FORM */
        <div className="cost-form-page">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3>{modalType === 'edit' ? 'Edit Cost' : 'Add Cost'}</h3> {/* Use modalType here */}
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
          </div>

          <Form onSubmit={handleSubmit(onSubmit)} noValidate>
            {/* TITLE */}
            <Form.Group className="mb-3">
              <Form.Label>Title / Group Name</Form.Label>
              <Controller
                name="title"
                control={control}
                rules={{ required: 'Title is required.' }}
                render={({ field }) => (
                  <Form.Control
                    {...field} // Spreading field props (value, onChange, onBlur, name)
                    placeholder="Enter title (e.g., Material Costs)"
                    isInvalid={!!errors.title} // Show validation feedback
                  />
                )}
              />
              {errors.title && <Form.Control.Feedback type="invalid">{errors.title.message}</Form.Control.Feedback>}
            </Form.Group>

            {/* TYPE */}
            <Form.Group className="mb-3">
              <Form.Label>Type</Form.Label>
              <Controller
                name="type"
                control={control}
                rules={{ required: 'Type is required.' }}
                render={({ field }) => (
                  <Form.Select
                    {...field} // Spreading field props
                    isInvalid={!!errors.type}
                  >
                    <option value="">-- Select Type --</option> {/* Ensure this value is not submitted */}
                    {lookupGroups.map(lk => (
                      <option key={lk.lookupGroup} value={lk.lookupGroup}>
                        {formatTypeName(lk.lookupGroup)}
                      </option>
                    ))}
                  </Form.Select>
                )}
              />
              {errors.type && <Form.Control.Feedback type="invalid">{errors.type.message}</Form.Control.Feedback>}
            </Form.Group>

            {/* VALUE TYPE */}
            <Form.Group className="mb-3">
              <Form.Label>Value Type</Form.Label>
              <Controller
                name="valueType"
                control={control}
                render={({ field }) => (
                  <Form.Select {...field}>
                    <option value="">-- Select Value Type --</option>
                    <option value="fix">Fixed</option>
                    <option value="percentage">Percentage (%)</option>
                  </Form.Select>
                )}
              />
            </Form.Group>

            {/* VALUES (Details section) */}
            {fields.length > 0 && (
              <div className="mt-4 mb-3">
                <h5 className="mb-3">Items / Values</h5>
                {fields.map((item, i) => (
                  <Row key={item.id} className="mb-2 align-items-center">
                    <Col md={6} className="mb-2 mb-md-0">
                      <Controller
                        name={`details.${i}.label`}
                        control={control}
                        render={({ field }) => (
                          <Form.Control {...field} readOnly placeholder="Label" />
                        )}
                      />
                    </Col>
                    <Col md={6}>
                      <Controller
                        name={`details.${i}.value`}
                        control={control}
                        rules={{
                          validate: (value) => {
                            if (value === '' || value === null || value === undefined) {
                              return true;
                            }
                            if (isNaN(Number(value))) {
                              return 'Value must be a number.';
                            }
                            return true;
                          }
                        }}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            placeholder="Enter value (optional)"
                            type="text"
                            isInvalid={!!errors.details?.[i]?.value}
                          />
                        )}
                      />
                      {errors.details?.[i]?.value && (
                        <Form.Control.Feedback type="invalid">
                          {errors.details[i].value.message}
                        </Form.Control.Feedback>
                      )}
                    </Col>
                  </Row>
                ))}
              </div>
            )}

            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Save Cost'}
            </Button>
          </Form>
        </div>
      )}
    </div>
  );
};

export default AdminCostPage;